/**
 * Slideshow timer: advances an index on an interval while playing.
 *
 * Restores the autoplay/pause behavior that was stripped from the legacy
 * Slideshow during the infinite-re-render fixes. Pure timer logic so it can be
 * unit-tested with fake timers; the consumer owns the index.
 */
import { useEffect, useRef, useState } from 'react';

interface UseSlideshowOptions {
  count: number;
  intervalMs?: number;
  onAdvance: (nextIndex: number) => void;
  getIndex: () => number;
  /** Called when a non-looping slideshow reaches the end. */
  onEnd?: () => void;
  loop?: boolean;
}

export interface SlideshowControls {
  playing: boolean;
  intervalMs: number;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  setIntervalMs: (ms: number) => void;
}

export function useSlideshow({
  count,
  intervalMs = 3500,
  onAdvance,
  getIndex,
  onEnd,
  loop = true,
}: UseSlideshowOptions): SlideshowControls {
  const [playing, setPlaying] = useState(false);
  const [interval, setIntervalState] = useState(intervalMs);
  // Keep latest callbacks in refs so the timer effect doesn't re-subscribe.
  const advanceRef = useRef(onAdvance);
  const indexRef = useRef(getIndex);
  const endRef = useRef(onEnd);
  advanceRef.current = onAdvance;
  indexRef.current = getIndex;
  endRef.current = onEnd;

  useEffect(() => {
    if (!playing || count <= 1) return;
    const id = setInterval(() => {
      const current = indexRef.current();
      const next = current + 1;
      if (next >= count) {
        if (loop) {
          advanceRef.current(0);
        } else {
          setPlaying(false);
          endRef.current?.();
        }
      } else {
        advanceRef.current(next);
      }
    }, interval);
    return () => clearInterval(id);
  }, [playing, interval, count, loop]);

  return {
    playing,
    intervalMs: interval,
    play: () => setPlaying(true),
    pause: () => setPlaying(false),
    toggle: () => setPlaying((p) => !p),
    setIntervalMs: setIntervalState,
  };
}
